/**
 * Detect which coding agents have rules/skills directories in `cwd`.
 * `clawnify skills add` installs into ALL detected agents simultaneously
 * (per Riccardo's rule), so this returns every agent whose marker is
 * present rather than picking one.
 *
 * Agent priority order doesn't matter for installation, but listing
 * matches the priority for "no agent detected" diagnostics.
 */

import { existsSync, statSync } from "node:fs";
import { join } from "node:path";

export type Agent = "claude" | "cursor" | "openclaw" | "codex" | "aider";

export interface DetectedAgent {
  agent: Agent;
  /** Where we'll write the skill. Relative to cwd. */
  target: string;
  /** Why this agent was detected. Shown in `--verbose` output. */
  marker: string;
}

interface Detector {
  agent: Agent;
  /** Files/dirs whose presence triggers detection. */
  markers: string[];
  /** Where the skill goes — directory or file path, relative to cwd. */
  target: string;
}

const DETECTORS: Detector[] = [
  { agent: "claude",   markers: [".claude", ".claude.json", "CLAUDE.md"],         target: ".claude/skills" },
  { agent: "cursor",   markers: [".cursor", ".cursorrules"],                       target: ".cursor/rules" },
  { agent: "openclaw", markers: [".openclaw", "openclaw.json"],                    target: ".openclaw/skills" },
  { agent: "codex",    markers: [".codex", "AGENTS.md"],                            target: ".codex/skills" },
  { agent: "aider",    markers: ["CONVENTIONS.md", ".aider.conf.yml"],              target: "CONVENTIONS.md" },
];

function exists(cwd: string, name: string): boolean {
  const path = join(cwd, name);
  try {
    statSync(path);
    return true;
  } catch {
    return false;
  }
}

export function detectAgents(cwd: string): DetectedAgent[] {
  const found: DetectedAgent[] = [];
  for (const d of DETECTORS) {
    const hit = d.markers.find((m) => exists(cwd, m));
    if (hit) {
      found.push({ agent: d.agent, target: d.target, marker: hit });
    }
  }
  return found;
}

/**
 * Force-detect a specific agent by name (used by `--agent` override).
 * Returns the detector entry even if no marker is present — the caller
 * is opting in explicitly.
 */
export function agentByName(name: Agent): DetectedAgent {
  const d = DETECTORS.find((x) => x.agent === name);
  if (!d) throw new Error(`Unknown agent: ${name}`);
  return { agent: d.agent, target: d.target, marker: "(forced)" };
}

export const ALL_AGENTS: Agent[] = DETECTORS.map((d) => d.agent);
